// useHandlers hook for FilterDrawer
import { useCallback } from 'react'
import { useAppDispatch } from '@user/store/hooks'
import {
  IUserPreferenceGeneralListItemType,
  saveGeneralPreferencesBatch,
} from '@user/store/userPreferences/userPreferencesGeneral'
import { useSalesPersonFilterEnabled } from '@user/store/tenantFeatures/hooks/useSalesPersonFilterEnabled'

export function buildKnockStatusesPref(
  d2dKnockEnabled: boolean,
  selectedKnockStatuses: string[],
  allKnockStatusIds: string[],
) {
  if (!d2dKnockEnabled) return []
  // Guard: if KS haven't loaded yet, don't write prefs — would convert '' sentinel to 'none'
  if (allKnockStatusIds.length === 0) return []
  const valid = selectedKnockStatuses.filter((id) => id && id.trim() !== '')
  return [
    {
      type: IUserPreferenceGeneralListItemType.MapViewFilteredKnockStatuses,
      value: valid.length > 0 ? valid.join('_') : 'none',
    },
    {
      type: IUserPreferenceGeneralListItemType.MapViewKnownKnockStatusIds,
      value: allKnockStatusIds.filter((id) => id && id.trim() !== '').join('_'),
    },
  ]
}

export function useHandlers({
  selectedTypes,
  setSelectedTypes,
  myRecords,
  setMyRecords,
  showFilterChips,
  setShowFilterChips,
  setShowValidation,
  initialSelectedTypes,
  initialMyRecords,
  initialSelectedKnockStatuses,
  selectedKnockStatuses,
  setSelectedKnockStatuses,
  closeAllDrawers,
  onClose,
  d2dKnockEnabled,
  knockStatuses,
}) {
  const dispatch = useAppDispatch()
  const salesPersonFilterEnabled = useSalesPersonFilterEnabled()

  const handleCheckboxChange = useCallback(
    (type: string) => {
      setShowValidation(false)
      setSelectedTypes((prev) => {
        if (prev.includes(type)) {
          return prev.filter((t) => t !== type)
        } else {
          return [...prev, type]
        }
      })
    },
    [setSelectedTypes, setShowValidation],
  )

  const handleKnockStatusCheckboxChange = useCallback(
    (statusId: string) => {
      setSelectedKnockStatuses((prev: string[]) => {
        if (prev.includes(statusId)) {
          return prev.filter((id: string) => id !== statusId)
        } else {
          return [...prev, statusId]
        }
      })
    },
    [setSelectedKnockStatuses],
  )

  // FR6: retired in favor of the standalone salesperson filter. Kept as a
  // no-op behind the flag rather than deleted, so flipping the flag back off
  // restores the exact old behavior with no further code changes.
  const onChangeMyRecords = useCallback(
    (_e, checked: boolean) => {
      if (salesPersonFilterEnabled) return
      setMyRecords(checked)
    },
    [setMyRecords, salesPersonFilterEnabled],
  )

  const onChangeShowFilterChips = useCallback(
    (_e, checked: boolean) => {
      setShowFilterChips(checked)
    },
    [setShowFilterChips],
  )

  const handleViewResults = useCallback(async () => {
    const hasNoRecordTypes =
      selectedTypes.filter((type) => type.trim() !== '').length === 0
    const hasKnockStatusSelected =
      selectedKnockStatuses.filter((id) => id && id.trim() !== '').length > 0
    let isValid = true
    if (hasNoRecordTypes && (!d2dKnockEnabled || !hasKnockStatusSelected)) {
      setShowValidation(true)
      isValid = false
    }

    // Check if any filter has changed
    const filtersChanged =
      initialMyRecords !== myRecords ||
      initialSelectedTypes.length !== selectedTypes.length ||
      initialSelectedTypes.some((type, idx) => type !== selectedTypes[idx]) ||
      initialSelectedKnockStatuses.length !== selectedKnockStatuses.length ||
      initialSelectedKnockStatuses.some(
        (id: string, idx: number) => id !== selectedKnockStatuses[idx],
      )

    const knockStatusesPref = buildKnockStatusesPref(
      d2dKnockEnabled,
      selectedKnockStatuses,
      knockStatuses.map((ks) => String(ks.id)),
    )

    await dispatch(
      saveGeneralPreferencesBatch([
        ...(salesPersonFilterEnabled
          ? []
          : [
              {
                type: IUserPreferenceGeneralListItemType.MapViewFilteredMyRecords,
                value: myRecords ? 'true' : 'false',
              },
            ]),
        {
          type: IUserPreferenceGeneralListItemType.ShowMapViewFilterChips,
          value: showFilterChips ? 'true' : 'false',
        },
        {
          type: IUserPreferenceGeneralListItemType.MapViewFilteredEntityTypes,
          value: selectedTypes.filter((t) => t && t.trim() !== '').join('_'),
        },
        ...knockStatusesPref,
      ]),
    )
    if (isValid) {
      if (filtersChanged) {
        closeAllDrawers()
      }
      onClose()
    }
  }, [
    dispatch,
    myRecords,
    onClose,
    selectedTypes,
    showFilterChips,
    selectedKnockStatuses,
    closeAllDrawers,
    initialMyRecords,
    initialSelectedTypes,
    initialSelectedKnockStatuses,
    setShowValidation,
    d2dKnockEnabled,
    knockStatuses,
    salesPersonFilterEnabled,
  ])

  return {
    handleCheckboxChange,
    handleKnockStatusCheckboxChange,
    onChangeMyRecords,
    onChangeShowFilterChips,
    handleViewResults,
  }
}
